// MemberPage.tsx - Version revampée avec menu latéral responsive
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { initializeDefaultBadges } from '../components/BadgeProgression';
import { useMemberCard } from '../hooks/useMemberCard';
import { PERMISSION_MATRIX, MembershipLevel, MembershipPermissions } from '../types/adhesion.types';

// Composants réutilisables
import MemberLogin from '../components/MemberLogin';

// Composants des sections
import MemberDashboard from '../components/member/MemberDashboard';
import MemberProfileComplete from '../components/member/MemberProfileComplete';

// Composants d'engagement citoyen
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import MemberCivicEngagement from '../components/member/MemberCivicEngagement';

// Composants des nouveaux espaces membres
import MemberFormation from '../components/member/MemberFormation';
import MemberVolunteer from '../components/member/MemberVolunteer';
import MemberSurveys from '../components/member/MemberSurveys';
import MemberProjects from '../components/member/MemberProjects';
import MemberSocial from '../components/member/MemberSocial';

import {
    Home,
    Users,
    Menu,
    X,
    LogOut,
    Star,
    Shield,
    User,
    GraduationCap,
    MessageSquare,
    Lightbulb,
    Newspaper
} from 'lucide-react';

type MenuSection = 'dashboard' | 'formation' | 'volunteer' | 'surveys' | 'projects' | 'social' | 'profile';

interface MenuItem {
    id: MenuSection;
    label: string;
    icon: React.ReactNode;
    requiredPermission?: keyof MembershipPermissions;
    badge?: string;
    mobileOnly?: boolean;
}

const MemberPage: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [memberData, setMemberData] = useState<any>(null);
    const [activeSection, setActiveSection] = useState<MenuSection>('dashboard');
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Mapper les URL vers les sections
    const urlToSection = (pathname: string): MenuSection => {
        if (pathname.includes('/projets')) return 'projects';
        if (pathname.includes('/enquetes')) return 'surveys';
        if (pathname.includes('/benevolat')) return 'volunteer';
        if (pathname.includes('/formations')) return 'formation';
        if (pathname.includes('/social')) return 'social';
        return 'dashboard';
    };

    // Mapper les sections vers les URL
    const sectionToUrl = (section: MenuSection): string => {
        switch (section) {
            case 'projects': return '/membre/projets';
            case 'surveys': return '/membre/enquetes';
            case 'volunteer': return '/membre/benevolat';
            case 'formation': return '/membre/formations';
            case 'social': return '/membre/social';
            default: return '/membre';
        }
    };

    // Hook pour la carte de membre
    const {
        // États
        qrCodeUrl,
        showQRModal,
        isDownloadingCard,
        isDownloadingPDF,
        downloadError,
        pdfError,
        copySuccess,

        // Fonctions principales
        downloadMemberCard,
        generateAdhesionFormPDF,
        copyReferralLink,
        shareWhatsApp,
        shareToSocial,

        // Fonctions QR Code
        openQRModal,
        closeQRModal,
        downloadQRCode,

        // Fonctions utilitaires
        clearDownloadError,
        clearPdfError,
        generateShareLinks
    } = useMemberCard(memberData);

    useEffect(() => {
        initializeDefaultBadges();
    }, []);

    // Synchroniser l'URL et la section active (effet unique pour éviter les boucles)
    useEffect(() => {
        const currentSection = urlToSection(location.pathname);
        // Seulement mettre à jour si la section détectée depuis l'URL est différente
        if (currentSection !== activeSection) {
            setActiveSection(currentSection);
        }
        // Note: On ne navigue PAS ici pour éviter la boucle infinie
        // La navigation est gérée uniquement par handleSectionChange()
    }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

    // Clés localStorage pour la persistance
    const STORAGE_KEYS = {
        isAuthenticated: 'udb_member_authenticated',
        memberData: 'udb_member_data',
        activeSection: 'udb_member_section'
    };

    // Fonctions de persistance
    const saveToStorage = (key: string, value: any) => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.warn('Erreur sauvegarde localStorage:', error);
        }
    };

    const loadFromStorage = (key: string) => {
        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : null;
        } catch (error) {
            console.warn('Erreur lecture localStorage:', error);
            return null;
        }
    };

    const clearStorage = () => {
        try {
            Object.values(STORAGE_KEYS).forEach(key => {
                localStorage.removeItem(key);
            });
        } catch (error) {
            console.warn('Erreur nettoyage localStorage:', error);
        }
    };

    // Vérifier si la session a expiré (24h)
    const isSessionExpired = () => {
        const sessionTimestamp = loadFromStorage('udb_member_session_timestamp');
        if (!sessionTimestamp) return true;
        
        const now = Date.now();
        const sessionAge = now - sessionTimestamp;
        const maxAge = 24 * 60 * 60 * 1000; // 24 heures
        
        return sessionAge > maxAge;
    };

    // Charger les données au démarrage
    useEffect(() => {
        console.log('🔄 Chargement des données persistantes...');
        
        const savedAuth = loadFromStorage(STORAGE_KEYS.isAuthenticated);
        const savedMemberData = loadFromStorage(STORAGE_KEYS.memberData);
        const savedSection = loadFromStorage(STORAGE_KEYS.activeSection);

        if (savedAuth && savedMemberData && !isSessionExpired()) {
            console.log('✅ Session membre valide trouvée, restauration...');
            setIsAuthenticated(true);
            setMemberData(savedMemberData);
            if (savedSection) {
                setActiveSection(savedSection);
            }
        } else {
            if (isSessionExpired()) {
                console.log('⏰ Session expirée, nettoyage...');
                clearStorage();
            } else {
                console.log('❌ Aucune session membre trouvée');
            }
        }
        
        setIsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Gestion de l'authentification
    const handleAuthenticated = (member: any) => {
        console.log('🔐 Authentification membre réussie, sauvegarde...');
        setMemberData(member);
        setIsAuthenticated(true);
        
        // Sauvegarder dans localStorage avec timestamp
        saveToStorage(STORAGE_KEYS.isAuthenticated, true);
        saveToStorage(STORAGE_KEYS.memberData, member);
        saveToStorage('udb_member_session_timestamp', Date.now());
    };

    const handleLogout = () => {
        console.log('🚪 Déconnexion membre, nettoyage...');
        setIsAuthenticated(false);
        setMemberData(null);
        clearStorage();
        handleSectionChange('dashboard');
    };

    // Validation du format NIP
    const isValidNip = (nip: string): boolean => {
        if (!nip || typeof nip !== 'string') return false;
        // Format: XX-XXXX-XXXXXXXX (lettres et chiffres)
        const nipPattern = /^[A-Za-z0-9]{2}-[A-Za-z0-9]{4}-[A-Za-z0-9]{8}$/;
        return nipPattern.test(nip.trim());
    };

    // Obtenir le niveau de membre
    const getMembershipLevel = (): MembershipLevel => {
        if (!memberData) return 'sympathisant';
        
        // PRIORITÉ 1: Présence d'un NIP VALIDE (membre complet)
        // Vérifier toutes les variantes possibles (adhésions numériques ET papier)
        const nipVariants = [
            memberData?.nip,
            memberData?.NIP,
            memberData?.nip_number,
            memberData?.member_nip,
            memberData?.identity_number,
            memberData?.nip_code,           // Champ potentiel adhésions papier
            memberData?.numero_nip,         // Variante française
            memberData?.member_identity,    // Autre variante possible
            memberData?.national_id,        // ID national
            memberData?.numero_identite     // Numéro d'identité
        ].filter(Boolean);
        
        // Vérifier si au moins un NIP est valide
        const validNip = nipVariants.find(nip => isValidNip(nip as string));
        
        if (validNip) {
            console.log('✅ NIP valide détecté:', validNip, '- Classification: full_member');
            return 'full_member';
        }
        
        // Debug: afficher tous les champs NIP trouvés mais non valides
        if (nipVariants.length > 0) {
            console.log('⚠️ NIPs trouvés mais format invalide:', nipVariants);
        }
        
        // PRIORITÉ 2: Champ membership_level explicite (si pas de NIP valide)
        if (memberData?.membership_level && memberData.membership_level !== 'sympathisant') {
            console.log('📋 Niveau explicite trouvé:', memberData.membership_level);
            return memberData.membership_level;
        }
        
        // PRIORITÉ 3: Email seulement (membre partiel)
        if (memberData?.email) {
            console.log('📧 Email détecté sans NIP - Classification: partial_member');
            return 'partial_member';
        }
        
        console.log('👤 Aucun critère de membre complet - Classification: sympathisant');
        return 'sympathisant';
    };

    // Obtenir les permissions du membre
    const getPermissions = () => {
        const level = getMembershipLevel();
        return PERMISSION_MATRIX[level];
    };

    // Configuration du menu simplifié
    const getMenuItems = (): MenuItem[] => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const permissions = getPermissions();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const level = getMembershipLevel();
        
        return [
            {
                id: 'dashboard' as MenuSection,
                label: 'Tableau de bord',
                icon: <Home className="w-5 h-5" />
            },
            {
                id: 'social' as MenuSection,
                label: 'UDB News',
                icon: <Newspaper className="w-5 h-5" />,
                badge: 'Nouveau'
            },
            {
                id: 'formation' as MenuSection,
                label: 'Formation',
                icon: <GraduationCap className="w-5 h-5" />
            },
            {
                id: 'volunteer' as MenuSection,
                label: 'Bénévolat',
                icon: <Users className="w-5 h-5" />
            },
            {
                id: 'surveys' as MenuSection,
                label: 'Enquêtes',
                icon: <MessageSquare className="w-5 h-5" />
            },
            {
                id: 'projects' as MenuSection,
                label: 'Projets',
                icon: <Lightbulb className="w-5 h-5" />
            },
            {
                id: 'profile' as MenuSection,
                label: 'Mon profil',
                icon: <User className="w-5 h-5" />
            }
        ];
    };

    // Obtenir le titre de la section active
    const getSectionTitle = (): string => {
        const menuItems = getMenuItems();
        const activeItem = menuItems.find(item => item.id === activeSection);
        return activeItem?.label || 'Tableau de bord';
    };

    // Obtenir l'icône de niveau de membre
    const getMemberLevelIcon = () => {
        const level = getMembershipLevel();
        switch (level) {
            case 'full_member':
                return <Shield className="w-4 h-4 text-green-600" />;
            case 'partial_member':
                return <Star className="w-4 h-4 text-yellow-600" />;
            default:
                return <User className="w-4 h-4 text-blue-600" />;
        }
    };

    // Obtenir le libellé du niveau
    const getMemberLevelLabel = () => {
        const level = getMembershipLevel();
        switch (level) {
            case 'full_member': return 'Membre Complet';
            case 'partial_member': return 'Membre Partiel';
            case 'sympathisant': return 'Sympathisant';
            default: return 'Membre';
        }
    };

    // Fermer le sidebar mobile lors du changement de section et scroll to top
    const handleSectionChange = (section: MenuSection) => {
        console.log('🔄 Changement de section:', section);

        // Mettre à jour l'état local
        setActiveSection(section);
        setIsMobileSidebarOpen(false);

        // Naviguer vers l'URL correspondante
        const newUrl = sectionToUrl(section);
        if (location.pathname !== newUrl) {
            navigate(newUrl, { replace: true });
        }

        // Sauvegarder la section active
        if (isAuthenticated) {
            saveToStorage(STORAGE_KEYS.activeSection, section);
        }

        // Scroll to top de manière fluide
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    // Rendu du contenu de la section active
    const renderSectionContent = () => {
        const profileProps = {
            memberData,
            permissions: getPermissions(),
            membershipLevel: getMembershipLevel(),
            qrCodeUrl,
            showQRModal,
            isDownloadingCard,
            isDownloadingPDF,
            downloadError,
            pdfError,
            copySuccess,
            onQRClick: openQRModal,
            onCloseQRModal: closeQRModal,
            onDownloadCard: downloadMemberCard,
            onGeneratePDF: () => generateAdhesionFormPDF(memberData?.selfie_url),
            onClearDownloadError: clearDownloadError,
            onClearPdfError: clearPdfError,
            onDownloadQR: downloadQRCode,
            onCopyLink: copyReferralLink,
            onShareWhatsApp: shareWhatsApp,
            onShareSocial: shareToSocial,
            onUpdate: setMemberData,
            shareLinks: generateShareLinks()
        };

        switch (activeSection) {
            case 'dashboard':
                return (
                    <MemberDashboard 
                        memberData={memberData}
                        permissions={getPermissions()}
                        membershipLevel={getMembershipLevel()}
                        onSectionChange={handleSectionChange}
                    />
                );
            case 'formation':
                return <MemberFormation memberData={memberData} />;
            case 'volunteer':
                return <MemberVolunteer memberData={memberData} />;
            case 'surveys':
                return <MemberSurveys memberData={memberData} />;
            case 'projects':
                return <MemberProjects memberData={memberData} />;
            case 'social':
                return <MemberSocial memberData={memberData} />;
            case 'profile':
                return <MemberProfileComplete {...profileProps} />;
            default:
                return (
                    <MemberDashboard 
                        memberData={memberData}
                        permissions={getPermissions()}
                        membershipLevel={getMembershipLevel()}
                        onSectionChange={handleSectionChange}
                    />
                );
        }
    };

    // ==========================================
    // ÉCRAN DE CHARGEMENT
    // ==========================================
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Chargement de votre espace membre...</p>
                </div>
            </div>
        );
    }

    // ==========================================
    // PAGE DE CONNEXION
    // ==========================================
    if (!isAuthenticated) {
        return (
            <MemberLogin 
                onAuthenticated={handleAuthenticated}
            />
        );
    }

    const menuItems = getMenuItems();

    // ==========================================
    // PAGE MEMBRE AVEC LAYOUT SIDEBAR
    // ==========================================
    return (
        <div className="min-h-screen bg-gray-50 flex">
            {/* Sidebar Desktop */}
            <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 lg:pt-16 lg:bg-white lg:border-r lg:border-gray-200 z-40">
                <div className="flex flex-col flex-grow overflow-y-auto">
                    {/* Profil membre */}
                    <div className="flex-shrink-0 px-4 py-6 border-b border-gray-200">
                        <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                {memberData.first_name?.charAt(0)}{memberData.last_name?.charAt(0)}
                            </div>
                            <div className="ml-3">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                    {memberData.first_name} {memberData.last_name}
                                </p>
                                <div className="flex items-center space-x-1">
                                    {getMemberLevelIcon()}
                                    <p className="text-xs text-gray-500">{getMemberLevelLabel()}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 px-2 py-4 space-y-1">
                        {menuItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => handleSectionChange(item.id)}
                                className={`${
                                    activeSection === item.id
                                        ? 'bg-blue-50 border-r-2 border-blue-600 text-blue-700'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                } ${
                                    item.id === 'social'
                                        ? 'relative bg-gradient-to-r from-primary-blue/10 to-primary-yellow/10 border border-primary-blue/20 animate-pulse hover:from-primary-blue/20 hover:to-primary-yellow/20'
                                        : ''
                                } group flex items-center px-3 py-2 text-sm font-medium w-full transition-all duration-300`}
                            >
                                {item.icon}
                                <span className="ml-3 flex-1 text-left">{item.label}</span>
                                {item.badge && (
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                        item.id === 'social' ? 'bg-primary-yellow text-primary-blue animate-bounce' :
                                        activeSection === item.id ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                    }`}>
                                        {item.badge}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>

                    {/* Bouton déconnexion */}
                    <div className="flex-shrink-0 border-t border-gray-200 p-4">
                        <button
                            onClick={handleLogout}
                            className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md w-full transition-colors"
                        >
                            <LogOut className="w-5 h-5 mr-3" />
                            <span>Déconnexion</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Sidebar Mobile */}
            {isMobileSidebarOpen && (
                <div className="fixed inset-0 flex z-60 lg:hidden">
                    <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setIsMobileSidebarOpen(false)} />
                    <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-white">
                        <div className="absolute top-0 right-0 -mr-12 pt-2">
                            <button
                                type="button"
                                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                                onClick={() => setIsMobileSidebarOpen(false)}
                            >
                                <X className="h-6 w-6 text-white" />
                            </button>
                        </div>
                        <div className="flex-shrink-0 flex items-center px-4">
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-green-600 rounded-full flex items-center justify-center text-white font-bold">
                                UDB
                            </div>
                            <span className="ml-2 text-lg font-semibold text-gray-900">Espace Membre</span>
                        </div>
                        
                        {/* Profil mobile */}
                        <div className="mt-5 flex-shrink-0 px-4 py-4 border-b border-gray-200">
                            <div className="flex items-center">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-green-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                                    {memberData.first_name?.charAt(0)}{memberData.last_name?.charAt(0)}
                                </div>
                                <div className="ml-3">
                                    <p className="text-sm font-medium text-gray-900">
                                        {memberData.first_name} {memberData.last_name}
                                    </p>
                                    <div className="flex items-center space-x-1">
                                        {getMemberLevelIcon()}
                                        <p className="text-xs text-gray-500">{getMemberLevelLabel()}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <nav className="mt-5 flex-1 px-2 space-y-1">
                            {menuItems.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => handleSectionChange(item.id)}
                                    className={`${
                                        activeSection === item.id
                                            ? 'bg-blue-50 border-r-2 border-blue-600 text-blue-700'
                                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                    } ${
                                        item.id === 'social'
                                            ? 'relative bg-gradient-to-r from-primary-blue/10 to-primary-yellow/10 border border-primary-blue/20 animate-pulse hover:from-primary-blue/20 hover:to-primary-yellow/20'
                                            : ''
                                    } group flex items-center px-3 py-2 text-sm font-medium rounded-md w-full transition-all duration-300`}
                                >
                                    {item.icon}
                                    <span className="ml-3 flex-1 text-left">{item.label}</span>
                                    {item.badge && (
                                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                            item.id === 'social' ? 'bg-primary-yellow text-primary-blue animate-bounce' :
                                            activeSection === item.id ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                                        }`}>
                                            {item.badge}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </nav>
                        
                        <div className="flex-shrink-0 border-t border-gray-200 p-4">
                            <button
                                onClick={handleLogout}
                                className="flex items-center px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md w-full transition-colors"
                            >
                                <LogOut className="w-5 h-5 mr-3" />
                                <span>Déconnexion</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Contenu principal */}
            <div className="flex-1 lg:pl-64 pt-16">
                
                {/* Header mobile */}
                <div className="lg:hidden">
                    <div className="flex items-center justify-between bg-white px-4 py-3 border-b border-gray-200">
                        <button
                            type="button"
                            className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                            onClick={() => setIsMobileSidebarOpen(true)}
                        >
                            <Menu className="h-6 w-6" />
                        </button>
                        <h1 className="text-lg font-semibold text-gray-900">{getSectionTitle()}</h1>
                        <div className="w-10"></div> {/* Espaceur pour centrer le titre */}
                    </div>
                </div>

                {/* Contenu des sections */}
                <main className="flex-1">
                    <div className="py-6">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            {renderSectionContent()}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MemberPage;
